import { useCallback, useEffect } from 'react'

import { EntityKeys, queryClient } from 'utils/reactQuery'
import { SubscriptionPeriod, SubscriptionPlan } from 'models/user/userWorkspaceDTOs'
import { logger } from 'utils/logging'
import { useGetSubscriptionCheckoutLink } from 'dataQueries/subscriptions.query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

export function useSubscriptionCheckout(workspaceId: string) {
  const { t } = useTranslation(['workspace_settings'])
  const { spawnErrorToast } = useSnackbar()

  const checkoutLink = useGetSubscriptionCheckoutLink()

  useEffect(() => {
    if (checkoutLink.isError) {
      logger.error('Failed to fetch subscription checkout link', checkoutLink.error, {
        context: { component: 'PlanContent', action: 'getCheckoutLink' },
      })
      spawnErrorToast(t('workspace_settings:plan.checkout_error'))
      checkoutLink.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutLink.isError])

  const openCheckout = useCallback((period: SubscriptionPeriod) => {
    checkoutLink.mutate({
      workspaceId,
      period,
      plan: SubscriptionPlan.PRO_PER_SEAT,
    })
  }, [checkoutLink, workspaceId])

  const closeCheckout = useCallback(() => {
    checkoutLink.reset()
    queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE] })
    // Refresh the USER_ME query so the newly activated paid plan propagates to
    // currentUserWorkspace. The Plan page derives its plan from USER_ME (clientData.workspaces),
    // not from the invalidated USER_WORKSPACE query — without this it keeps rendering the
    // "choose a plan" table after a successful checkout.
    queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
  }, [checkoutLink])

  return {
    isCheckoutLoading: checkoutLink.isPending,
    isCheckoutOpen: checkoutLink.isSuccess,
    checkoutUrl: checkoutLink.data?.url ?? null,
    openCheckout,
    closeCheckout,
  }
}
